import { useEffect, useState } from 'react'
import moment, { Moment } from 'moment-timezone'
import { Daycare } from '@/types/Daycare'
import { getDaycareClosing, getDaycareOpening, getNextOpeningDate } from '@/modules/helper/daycareHelper'
import { getTextFromLanguage } from '@/modules/helper/languageHelper'

interface UseDateNavigationProps {
    daycare: Daycare
    defaultDate: string | null
    onDateChange?: (date: string, sectionIds: number[]) => void
}

interface UseDateNavigationReturn {
    date: string
    dayOpening: string
    dayClosing: string
    handleNextDate: () => void
    handlePreviousDate: () => void
    setDateValues: (date: Moment) => void
    isOpen: (day: number) => boolean
    selectedSectionAndSectionGroup: string[]
    setSelectedSectionAndSectionGroup: (selected: string[]) => void
    getSectionIdsFromSelection: (selection: string[]) => number[]
}

export function useDateNavigation({
    daycare,
    defaultDate,
    onDateChange
}: UseDateNavigationProps): UseDateNavigationReturn {
    function isOpen(day: number): boolean {
        return daycare ? getDaycareOpening(daycare, day) !== null && getDaycareClosing(daycare, day) !== null : false
    }

    const [date, setDate] = useState<string>(
        defaultDate
            ? defaultDate
            : daycare
              ? getNextOpeningDate(daycare, moment()).format('DD-MM-YYYY')
              : moment().format('DD-MM-YYYY')
    )

    const [dayOpening, setDayOpening] = useState<string>(
        getDaycareOpening(daycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7) || '08:00'
    )
    const [dayClosing, setDayClosing] = useState<string>(
        getDaycareClosing(daycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7) || '18:00'
    )

    const [selectedSectionAndSectionGroup, setSelectedSectionAndSectionGroup] = useState<string[]>([])

    function getSectionIdsFromSelection(selection: string[]): number[] {
        return selection
            .filter((s: string): boolean => s.startsWith('SECTION_'))
            .map((s: string): number => parseInt(s.replace('SECTION_', '')))
    }

    function handleNextDate(): void {
        const temp: Moment = moment(date, 'DD-MM-YYYY').add(1, 'day')
        let stop: number = 0
        while (!isOpen((moment(temp).day() + 6) % 7) && stop < 7) {
            temp.add(1, 'day')
            stop++
        }
        setDateValues(temp)
    }

    function handlePreviousDate(): void {
        const temp: Moment = moment(date, 'DD-MM-YYYY').subtract(1, 'day')
        let stop: number = 0
        while (!isOpen((moment(temp).day() + 6) % 7) && stop < 7) {
            temp.subtract(1, 'day')
            stop++
        }
        setDateValues(temp)
    }

    function setDateValues(newDate: Moment): void {
        const formatedDate: string = newDate.format('DD-MM-YYYY')
        setDate(formatedDate)
        setDayOpening(getDaycareOpening(daycare, (newDate.day() + 6) % 7) || '08:00')
        setDayClosing(getDaycareClosing(daycare, (newDate.day() + 6) % 7) || '18:00')

        const sectionIds: number[] = getSectionIdsFromSelection(selectedSectionAndSectionGroup)
        if (sectionIds.length > 0 && onDateChange) {
            onDateChange(formatedDate, sectionIds)
        }
    }

    useEffect(() => {
        moment.locale(getTextFromLanguage())
        setDateValues(moment(date, 'DD-MM-YYYY'))
    }, [])

    return {
        date,
        dayOpening,
        dayClosing,
        handleNextDate,
        handlePreviousDate,
        setDateValues,
        isOpen,
        selectedSectionAndSectionGroup,
        setSelectedSectionAndSectionGroup,
        getSectionIdsFromSelection
    }
}
